import { Dropdown, Grid, GridColumn, Input } from 'semantic-ui-react';
import { COMPANY_SUBTYPE_CODES, CompanySubtypeCode } from '~/types/company';
import { CompanyOwnershipDropdownOptions } from '~/constants';

export interface CompaniesFiltersState {
  companyName: string;
  companyTypes: string[];
  subtypeCodes: CompanySubtypeCode[];
}

const COMPANY_TYPE_OPTIONS = [
  { key: 'Investor', text: 'Investor', value: 'Investor' },
  { key: 'Developer', text: 'Developer', value: 'Developer' },
  { key: 'External', text: 'External', value: 'External' },
];

const SUBTYPE_OPTIONS = COMPANY_SUBTYPE_CODES.map(code => ({
  key: code,
  text: code,
  value: code,
}));

const CUSTOMER_TIER_FILTER_OPTIONS = CompanyOwnershipDropdownOptions.map(o => ({
  key: o.key,
  text: o.text,
  value: o.value,
}));

/** Admin companies list: filter by company.active */
export type CompanyActiveFilter = 'all' | 'active' | 'deactivated';

const COMPANY_STATUS_OPTIONS: { key: string; text: string; value: CompanyActiveFilter }[] = [
  { key: 'all', text: 'All', value: 'all' },
  { key: 'active', text: 'Active', value: 'active' },
  { key: 'deactivated', text: 'Deactivated', value: 'deactivated' },
];

interface AdminUserOption {
  id: string;
  name: string;
}

interface CompaniesFiltersProps {
  companyName: string;
  onCompanyNameChange: (value: string) => void;
  companyTypes: string[];
  onCompanyTypesChange: (value: string[]) => void;
  subtypeCodes: CompanySubtypeCode[];
  onSubtypeCodesChange: (value: CompanySubtypeCode[]) => void;
  ownerIds: string[];
  onOwnerIdsChange: (value: string[]) => void;
  thirdPartyExperiences: string[];
  onThirdPartyExperiencesChange: (value: string[]) => void;
  companyActiveFilter: CompanyActiveFilter;
  onCompanyActiveFilterChange: (value: CompanyActiveFilter) => void;
  adminUsers: AdminUserOption[];
}

const colStyle = { padding: '1rem 0 0 1rem' };
const controlFontSize = { fontSize: '.9rem' };

export default function CompaniesFilters({
  companyName,
  onCompanyNameChange,
  companyTypes,
  onCompanyTypesChange,
  subtypeCodes,
  onSubtypeCodesChange,
  ownerIds,
  onOwnerIdsChange,
  thirdPartyExperiences,
  onThirdPartyExperiencesChange,
  companyActiveFilter,
  onCompanyActiveFilterChange,
  adminUsers,
}: CompaniesFiltersProps) {
  const conductorLeadOptions = adminUsers.map(u => ({
    key: u.id,
    text: u.name,
    value: u.id,
  }));

  return (
    <div style={{ margin: '1rem 0 2rem', paddingRight: '1rem' }}>
      <Grid stackable>
        <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
          <Input
            type='text'
            placeholder='Company name'
            value={companyName}
            onChange={e => onCompanyNameChange(e.target.value)}
            fluid
            style={controlFontSize}
            autoFocus
          />
        </GridColumn>
        <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
          <Dropdown
            placeholder='Type'
            fluid
            multiple
            search
            selection
            clearable
            options={COMPANY_TYPE_OPTIONS}
            value={companyTypes}
            onChange={(_, { value }) => onCompanyTypesChange(value as string[])}
            style={controlFontSize}
            selectOnBlur={false}
          />
        </GridColumn>
        <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
          <Dropdown
            placeholder='Status'
            fluid
            selection
            options={COMPANY_STATUS_OPTIONS}
            value={companyActiveFilter}
            onChange={(_, { value }) =>
              onCompanyActiveFilterChange(value as CompanyActiveFilter)
            }
            style={controlFontSize}
            selectOnBlur={false}
          />
        </GridColumn>
        <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
          <Dropdown
            placeholder='Subtypes'
            fluid
            multiple
            search
            selection
            clearable
            options={SUBTYPE_OPTIONS}
            value={subtypeCodes}
            onChange={(_, { value }) => onSubtypeCodesChange(value as CompanySubtypeCode[])}
            style={controlFontSize}
            selectOnBlur={false}
          />
        </GridColumn>
        <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
          <Dropdown
            placeholder='Lead'
            fluid
            multiple
            search
            selection
            clearable
            options={conductorLeadOptions}
            value={ownerIds}
            onChange={(_, { value }) => onOwnerIdsChange(value as string[])}
            disabled={conductorLeadOptions.length === 0}
            style={controlFontSize}
            selectOnBlur={false}
          />
        </GridColumn>
        <GridColumn mobile={16} tablet={6} computer={1} style={colStyle}>
          <Dropdown
            placeholder='Tier'
            fluid
            multiple
            search
            selection
            clearable
            options={CUSTOMER_TIER_FILTER_OPTIONS}
            value={thirdPartyExperiences}
            onChange={(_, { value }) => onThirdPartyExperiencesChange(value as string[])}
            style={controlFontSize}
            selectOnBlur={false}
          />
        </GridColumn>
      </Grid>
    </div>
  );
}
